import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-hot-toast';
import { getApiBaseUrl } from '../../config/apiBase';
import { 
  Play, 
  Square, 
  FileText, 
  Activity,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  Server,
  BarChart3,
  AlertTriangle,
  PlayCircle,
  RotateCcw,
  MemoryStick,
  Hash,
  X,
  Link2
} from 'lucide-react';

const READ_ONLY_WORKERS = {
  dlr_processor: { parent: 'smpp_worker', label: 'SMPP Submit Worker' },
};

const WorkerDashboard = () => {
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [actionLoading, setActionLoading] = useState({});
  const [showLogsDrawer, setShowLogsDrawer] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState(null);

  useEffect(() => {
    fetchWorkers();
  }, []);

  const fetchWorkers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/admin/workers`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setWorkers(Array.isArray(data) ? data : []);
      } else if (response.status === 403) {
        toast.error('Access denied. Master Admin role required.');
      }
    } catch (error) {
      console.error('Error fetching workers:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = () => {
    setRefreshing(true);
    fetchWorkers();
  };

  const handleWorkerAction = async (workerCode, action) => {
    const key = `${workerCode}-${action}`;
    setActionLoading(prev => ({ ...prev, [key]: true }));
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/admin/workers/${workerCode}/${action}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        toast.success(data.message || `Worker ${action} successful`);
        setTimeout(fetchWorkers, 1000);
      } else {
        toast.error(data.error || `Failed to ${action} worker`);
      }
    } catch (error) {
      toast.error(`Failed to ${action} worker`);
    } finally {
      setActionLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  const formatUptime = (seconds) => {
    if (!seconds || seconds <= 0) return '—';
    const d = Math.floor(seconds / 86400);
    const h = Math.floor((seconds % 86400) / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    if (d > 0) return `${d}d ${h}h ${m}m`;
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const getStatusBadge = (status) => {
    const map = {
      running: { color: 'bg-green-100 text-green-700 border-green-200', icon: <CheckCircle className="w-3.5 h-3.5" /> },
      stopped: { color: 'bg-red-100 text-red-700 border-red-200', icon: <XCircle className="w-3.5 h-3.5" /> },
      error:   { color: 'bg-red-100 text-red-700 border-red-200', icon: <AlertTriangle className="w-3.5 h-3.5" /> },
    };
    const s = map[status] || { color: 'bg-gray-100 text-gray-600 border-gray-200', icon: <Clock className="w-3.5 h-3.5" /> };
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-semibold rounded-full border ${s.color}`}>
        {s.icon} {status?.toUpperCase()}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  const runningCount = workers.filter(w => w.status === 'running').length;
  const stoppedCount = workers.filter(w => w.status === 'stopped').length;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-indigo-600" />
            Worker Dashboard
          </h1>
          <p className="text-sm text-gray-500 mt-1">Live systemd worker status &amp; management</p>
        </div>
        <button
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium transition-colors"
        >
          <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Total Workers</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{workers.length}</p>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg"><Server className="w-6 h-6 text-blue-600" /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Running</p>
              <p className="text-3xl font-bold text-green-600 mt-1">{runningCount}</p>
            </div>
            <div className="p-3 bg-green-50 rounded-lg"><PlayCircle className="w-6 h-6 text-green-600" /></div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium text-gray-500 uppercase">Stopped</p>
              <p className="text-3xl font-bold text-red-600 mt-1">{stoppedCount}</p>
            </div>
            <div className="p-3 bg-red-50 rounded-lg"><Square className="w-6 h-6 text-red-600" /></div>
          </div>
        </div>
      </div>

      {/* Worker Cards */}
      <div className="space-y-4">
        {workers.map((worker) => (
          <div key={worker.code} className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-5">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                {/* Worker Info */}
                <div className="flex items-start gap-4 flex-1 min-w-0">
                  <div className={`p-3 rounded-lg flex-shrink-0 ${worker.status === 'running' ? 'bg-green-50' : 'bg-gray-50'}`}>
                    <Activity className={`w-6 h-6 ${worker.status === 'running' ? 'text-green-600' : 'text-gray-400'}`} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-3 flex-wrap">
                      <h3 className="text-base font-semibold text-gray-900">{worker.name}</h3>
                      {getStatusBadge(worker.status)}
                    </div>
                    <p className="text-sm text-gray-500 mt-0.5">{worker.description || worker.type}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span className="flex items-center gap-1" title="Worker Code">
                        <Hash className="w-3 h-3" /> {worker.code}
                      </span>
                      {worker.pid && (
                        <span className="flex items-center gap-1" title="Process ID">
                          PID: {worker.pid}
                        </span>
                      )}
                      {worker.uptime_seconds > 0 && (
                        <span className="flex items-center gap-1" title="Uptime">
                          <Clock className="w-3 h-3" /> {formatUptime(worker.uptime_seconds)}
                        </span>
                      )}
                      {worker.memory_usage && worker.memory_usage !== '0' && (
                        <span className="flex items-center gap-1" title="Memory">
                          <MemoryStick className="w-3 h-3" /> {worker.memory_usage}
                        </span>
                      )}
                      {worker.heartbeat_status === 'ACTIVE' && (
                        <span className="flex items-center gap-1 text-green-600">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span> Active
                        </span>
                      )}
                    </div>
                    {READ_ONLY_WORKERS[worker.code] && (
                      <div className="flex items-center gap-1.5 mt-1.5 text-xs text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-md px-2 py-1 w-fit">
                        <Link2 className="w-3 h-3" />
                        <span>Runs inside <strong>{READ_ONLY_WORKERS[worker.code].label}</strong> — control via parent service</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 flex-shrink-0">
                  {READ_ONLY_WORKERS[worker.code] ? (
                    <span className="text-xs text-gray-400 italic">Controlled by {READ_ONLY_WORKERS[worker.code].label}</span>
                  ) : worker.status === 'running' ? (
                    <>
                      <button
                        onClick={() => handleWorkerAction(worker.code, 'restart')}
                        disabled={actionLoading[`${worker.code}-restart`]}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg hover:bg-amber-100 disabled:opacity-50 text-xs font-medium transition-colors"
                      >
                        {actionLoading[`${worker.code}-restart`] ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5" />}
                        Restart
                      </button>
                      <button
                        onClick={() => handleWorkerAction(worker.code, 'stop')}
                        disabled={actionLoading[`${worker.code}-stop`]}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-lg hover:bg-red-100 disabled:opacity-50 text-xs font-medium transition-colors"
                      >
                        {actionLoading[`${worker.code}-stop`] ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Square className="w-3.5 h-3.5" />}
                        Stop
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleWorkerAction(worker.code, 'start')}
                      disabled={actionLoading[`${worker.code}-start`]}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-lg hover:bg-green-100 disabled:opacity-50 text-xs font-medium transition-colors"
                    >
                      {actionLoading[`${worker.code}-start`] ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
                      Start
                    </button>
                  )}
                  <button
                    onClick={() => { setSelectedWorker(worker); setShowLogsDrawer(true); }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-50 text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-100 text-xs font-medium transition-colors"
                  >
                    <FileText className="w-3.5 h-3.5" />
                    Logs
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}

        {workers.length === 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Server className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No workers found</p>
          </div>
        )}
      </div>

      {/* Logs Drawer */}
      {showLogsDrawer && selectedWorker && (
        <WorkerLogsDrawer
          worker={selectedWorker}
          onClose={() => { setShowLogsDrawer(false); setSelectedWorker(null); }}
        />
      )}
    </div>
  );
};

// Logs Drawer — reads real log files from backend
const WorkerLogsDrawer = ({ worker, onClose }) => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterLevel, setFilterLevel] = useState('ALL');
  const containerRef = useRef(null);

  useEffect(() => {
    fetchLogs();
  }, [worker.code]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${getApiBaseUrl()}/admin/workers/${worker.code}/logs?limit=300`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setLogs(data.logs || []);
      }
    } catch (error) {
      console.error('Error fetching logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredLogs = filterLevel === 'ALL' ? logs : logs.filter(l => l.level === filterLevel);

  const getLevelColor = (level) => {
    switch (level) {
      case 'ERROR': return 'text-red-400 bg-red-900/30';
      case 'WARNING': return 'text-yellow-400 bg-yellow-900/30';
      case 'INFO': return 'text-blue-400 bg-blue-900/30';
      case 'DEBUG': return 'text-gray-400 bg-gray-700/50';
      default: return 'text-gray-400 bg-gray-700/50';
    }
  };

  const formatTime = (ts) => {
    try {
      const d = new Date(ts);
      if (isNaN(d.getTime())) return '';
      return d.toLocaleString('en-US', { timeZone: 'Asia/Kolkata', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true });
    } catch { return ''; }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-3xl bg-gray-900 h-full flex flex-col shadow-2xl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 bg-gray-800 border-b border-gray-700">
          <div className="flex items-center gap-3">
            <FileText className="w-5 h-5 text-green-400" />
            <div>
              <span className="text-white font-medium text-sm">{worker.name}</span>
              <span className="text-gray-400 text-xs ml-2">({worker.code})</span>
            </div>
            {worker.status === 'running' && (
              <span className="px-2 py-0.5 text-[10px] bg-green-600 text-white rounded-full font-medium">LIVE</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <select
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value)}
              className="px-2 py-1 text-xs bg-gray-700 text-gray-300 border border-gray-600 rounded"
            >
              <option value="ALL">All Levels</option>
              <option value="ERROR">Errors</option>
              <option value="WARNING">Warnings</option>
              <option value="INFO">Info</option>
              <option value="DEBUG">Debug</option>
            </select>
            <button onClick={fetchLogs} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors">
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-white hover:bg-gray-700 rounded transition-colors">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Log entries */}
        <div ref={containerRef} className="flex-1 overflow-y-auto p-4 font-mono text-xs leading-relaxed">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <RefreshCw className="w-6 h-6 animate-spin text-gray-500" />
            </div>
          ) : filteredLogs.length > 0 ? (
            <div className="space-y-1">
              {filteredLogs.map((log, i) => (
                <div key={i} className="flex items-start gap-2 py-1 hover:bg-gray-800/50 rounded px-2 -mx-2">
                  <span className="text-gray-500 flex-shrink-0 w-24 text-right">{formatTime(log.timestamp)}</span>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold flex-shrink-0 w-16 text-center ${getLevelColor(log.level)}`}>
                    {log.level}
                  </span>
                  <span className="text-gray-300 break-all">{log.message}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-32 text-gray-500">
              <div className="text-center">
                <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>No logs available</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-2 bg-gray-800 border-t border-gray-700 flex items-center justify-between text-xs text-gray-400">
          <span>{filteredLogs.length} entries {filterLevel !== 'ALL' ? `(filtered: ${filterLevel})` : ''}</span>
          <span>{logs[0]?.source ? `Source: ${logs[0]?.source}` : ''}</span>
        </div>
      </div>
    </div>
  );
};

export default WorkerDashboard;
